import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Wallet,
  PieChart, BarChart3, Settings
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/',          label: 'Home',     icon: LayoutDashboard },
  { path: '/balance',   label: 'Balance',  icon: Wallet          },
  { path: '/budget',    label: 'Budget',   icon: PieChart        },
  { path: '/reports',   label: 'Reports',  icon: BarChart3       },
  { path: '/settings',  label: 'Settings', icon: Settings        },
];

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();

  function isActive(path) {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }

  return (
    <nav style={{
      position: 'fixed',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100%',
      maxWidth: 480,
      background: 'var(--nav-bg)',
      borderTop: '1px solid var(--nav-border)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      display: 'flex',
      alignItems: 'stretch',
      zIndex: 100,
      // Bottom padding = safe area so content clears the iPhone home indicator.
      // Do NOT set a fixed total height — let the content + padding define it.
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      boxSizing: 'border-box',
    }}>
      {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
        const active = isActive(path);
        return (
          <button
            key={path}
            onClick={() => navigate(path)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              // Fixed height for the tappable button area only — safe area
              // padding above adds extra space at the bottom of the nav container
              // without making the tappable area taller than needed.
              height: 56,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 2px 4px',
              color: active ? 'var(--accent)' : 'var(--text-muted)',
              WebkitTapHighlightColor: 'transparent',
              minWidth: 0,
              position: 'relative',
            }}
          >
            {/* Active pill background behind icon */}
            <div style={{
              width: 44,
              height: 30,
              borderRadius: 15,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: active ? 'var(--accent-light)' : 'transparent',
              transition: 'background 0.2s',
              position: 'relative',
            }}>
              <Icon size={19} strokeWidth={active ? 2.5 : 1.75} />
              {/* Active dot above icon */}
              {active && (
                <div style={{
                  position: 'absolute',
                  top: -4,
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: 'var(--accent)',
                }} />
              )}
            </div>

            {/* Label — always visible, bold when active */}
            <span style={{
              fontSize: '0.5625rem',
              fontWeight: active ? 700 : 400,
              letterSpacing: '0.01em',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              maxWidth: '100%',
              textOverflow: 'ellipsis',
            }}>
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// Desktop sidebar — unchanged
export function DesktopNav() {
  const location = useLocation();
  const navigate = useNavigate();

  function isActive(path) {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }

  return (
    <nav style={{
      width: 220,
      height: '100vh',
      background: 'var(--bg-surface)',
      borderRight: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      padding: '24px 12px',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 100,
    }}>
      <div style={{ padding: '0 8px 24px', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, var(--accent) 0%, #6366F1 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: '1rem' }}>💰</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: '1.0625rem' }}>MyFinance</span>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
          const active = isActive(path);
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', borderRadius: 'var(--radius-md)',
                background: active ? 'var(--accent-light)' : 'transparent',
                border: 'none', cursor: 'pointer',
                color: active ? 'var(--accent)' : 'var(--text-secondary)',
                fontFamily: 'var(--font-ui)', fontSize: '0.9375rem',
                fontWeight: active ? 600 : 400, width: '100%', textAlign: 'left',
                transition: 'all 0.15s',
              }}
            >
              <Icon size={18} strokeWidth={active ? 2.5 : 1.75} />
              {label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
